import React, { useState, useCallback } from 'react';
import './HotelSearch.css';

function HotelSearch({ onSearch, loading }) {
  const [formData, setFormData] = useState({
    cityCode: '',
    checkIn: '',
    checkOut: '',
    guests: '1',
    rooms: '1',
    starRating: 'any',
  });

  const [errors, setErrors] = useState({});

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.cityCode.trim()) {
      newErrors.cityCode = 'City code is required';
    } else if (!/^[A-Z]{3}$/.test(formData.cityCode.toUpperCase())) {
      newErrors.cityCode = 'Enter a valid 3-letter city code (e.g., NYC, DEN)';
    }

    if (!formData.checkIn) {
      newErrors.checkIn = 'Check-in date is required';
    }

    if (!formData.checkOut) {
      newErrors.checkOut = 'Check-out date is required';
    }

    if (formData.checkIn && formData.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      if (checkOut <= checkIn) {
        newErrors.checkOut = 'Check-out must be after check-in';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      onSearch({
        ...formData,
        cityCode: formData.cityCode.toUpperCase(),
        guests: parseInt(formData.guests),
        rooms: parseInt(formData.rooms),
      });
    },
    [formData, validateForm, onSearch]
  );

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minCheckInDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="hotel-search">
      <div className="search-container">
        <div className="search-header">
          <h2>Find Your Perfect Hotel</h2>
          <p className="search-subtitle">
            This prototype uses Sabre Hotel Shopping v2 (REST API) for hotel availability.
          </p>
        </div>

        <form className="search-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cityCode">Destination</label>
              <input
                id="cityCode"
                name="cityCode"
                type="text"
                placeholder="e.g., NYC, DEN, LON"
                value={formData.cityCode}
                onChange={handleChange}
                disabled={loading}
                maxLength="3"
                className={errors.cityCode ? 'input-error' : ''}
              />
              {errors.cityCode && (
                <span className="error-text">{errors.cityCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="checkIn">Check-in</label>
              <input
                id="checkIn"
                name="checkIn"
                type="date"
                value={formData.checkIn}
                onChange={handleChange}
                disabled={loading}
                min={minCheckInDate}
                className={errors.checkIn ? 'input-error' : ''}
              />
              {errors.checkIn && (
                <span className="error-text">{errors.checkIn}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="checkOut">Check-out</label>
              <input
                id="checkOut"
                name="checkOut"
                type="date"
                value={formData.checkOut}
                onChange={handleChange}
                disabled={loading}
                min={formData.checkIn || minCheckInDate}
                className={errors.checkOut ? 'input-error' : ''}
              />
              {errors.checkOut && (
                <span className="error-text">{errors.checkOut}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="guests">Guests</label>
              <select
                id="guests"
                name="guests"
                value={formData.guests}
                onChange={handleChange}
                disabled={loading}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'Guest' : 'Guests'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rooms">Rooms</label>
              <select
                id="rooms"
                name="rooms"
                value={formData.rooms}
                onChange={handleChange}
                disabled={loading}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'Room' : 'Rooms'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="starRating">Star Rating</label>
              <select
                id="starRating"
                name="starRating"
                value={formData.starRating}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="any">Any</option>
                <option value="3">3★+</option>
                <option value="4">4★+</option>
                <option value="5">5★</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="search-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Searching...
              </>
            ) : (
              'Search Hotels'
            )}
          </button>
        </form>
      </div>

      <div className="search-features">
        <div className="feature-box">
          <div className="feature-icon">🔍</div>
          <h3>Real-time Availability</h3>
          <p>Search actual hotel inventory and rates</p>
        </div>
        <div className="feature-box">
          <div className="feature-icon">⭐</div>
          <h3>Guest Ratings</h3>
          <p>Filter by star rating to find your perfect stay</p>
        </div>
        <div className="feature-box">
          <div className="feature-icon">🔄</div>
          <h3>Compare Options</h3>
          <p>Compare multiple hotels side-by-side</p>
        </div>
      </div>
    </div>
  );
}

export default HotelSearch;
